import { SourceCode } from "@/components/CodeGen/SourceCode";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMcpResourceContent } from "@/lib/hooks/useMcpInspector";
import { Copy, FileText } from "lucide-react";
import type { McpResource } from "./ResourceSelector";

interface ResourceViewerProps {
  selectedResource: McpResource | null;
  projectId: string;
}

// Helper function to determine language for syntax highlighting
function getLanguageFromResource(resource: McpResource | null): string {
  if (!resource) {
    return "text";
  }

  const { mimeType, name, uri } = resource;

  // Check MIME type first
  if (mimeType) {
    if (mimeType.includes("javascript")) {
      return "javascript";
    }
    if (mimeType.includes("typescript")) {
      return "typescript";
    }
    if (mimeType.includes("json")) {
      return "json";
    }
    if (mimeType.includes("xml")) {
      return "xml";
    }
    if (mimeType.includes("html")) {
      return "html";
    }
    if (mimeType.includes("css")) {
      return "css";
    }
    if (mimeType.includes("markdown")) {
      return "markdown";
    }
    if (mimeType.includes("yaml")) {
      return "yaml";
    }
    if (mimeType.includes("python")) {
      return "python";
    }
    if (mimeType.includes("java")) {
      return "java";
    }
    if (mimeType.includes("c++") || mimeType.includes("cpp")) {
      return "cpp";
    }
    if (mimeType.includes("shell") || mimeType.includes("bash")) {
      return "bash";
    }
  }

  // Fallback to file extension from name or URI
  const filename = name || uri.split("/").pop() || "";
  const extension = filename.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "js":
      return "javascript";
    case "ts":
      return "typescript";
    case "jsx":
      return "jsx";
    case "tsx":
      return "tsx";
    case "json":
      return "json";
    case "xml":
      return "xml";
    case "html":
      return "html";
    case "css":
      return "css";
    case "scss":
    case "sass":
      return "scss";
    case "md":
    case "markdown":
      return "markdown";
    case "yml":
    case "yaml":
      return "yaml";
    case "py":
      return "python";
    case "java":
      return "java";
    case "cpp":
    case "cxx":
    case "cc":
      return "cpp";
    case "c":
      return "c";
    case "sh":
    case "bash":
      return "bash";
    case "sql":
      return "sql";
    case "php":
      return "php";
    case "rb":
      return "ruby";
    case "go":
      return "go";
    case "rs":
      return "rust";
    case "swift":
      return "swift";
    case "kt":
      return "kotlin";
    case "dart":
      return "dart";
    default:
      return "text";
  }
}

export function ResourceViewer({
  selectedResource,
  projectId,
}: ResourceViewerProps) {
  const {
    data: resourceContent,
    isLoading,
    error,
  } = useMcpResourceContent(
    projectId,
    selectedResource?.uri,
    !!selectedResource,
  );
  if (!selectedResource) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div>Select a resource to view its contents</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{selectedResource.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{selectedResource.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive">
            Failed to load resource content: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleCopyContent = async () => {
    if (resourceContent?.text) {
      await navigator.clipboard.writeText(resourceContent.text);
    }
  };

  const language = getLanguageFromResource(selectedResource);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{selectedResource.name}</CardTitle>
            {selectedResource.description && (
              <div className="text-sm text-muted-foreground">
                {selectedResource.description}
              </div>
            )}
          </div>
          {resourceContent?.text && (
            <Button variant="outline" size="sm" onClick={handleCopyContent}>
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {resourceContent ? (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">
              MIME Type: {resourceContent.mimeType || "unknown"}
            </div>
            {resourceContent.text ? (
              <div className="relative border rounded-lg overflow-hidden">
                <SourceCode
                  content={resourceContent.text}
                  language={language}
                  enableOverflow={true}
                  className="max-h-96"
                />
              </div>
            ) : resourceContent.blob ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <div className="mb-2">Binary content detected</div>
                <div className="text-xs">
                  Binary resources cannot be displayed as text
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <div className="mb-2">No content available</div>
                <div className="text-xs">This resource appears to be empty</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div className="mb-2">Failed to load resource content</div>
            <div className="text-xs">
              Unable to fetch content for this resource
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
